import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { GeoPoint } from '@app/shared/geo';
import { api, ApiError } from '../api';
import { DialogPortal } from '../ui/dialogHost';
import { useCompanySettings } from '../settings/company';
import { trapTab } from '../ui/focus';
import { MapPreview } from './MapPreview';
import { AddressSearch } from './AddressSearch';
import { PlaceSearch } from './PlaceSearch';
import { useGeoConfig } from '../geo/config';
import { mapChoicePatch, type MapChoice, type Place, type PlacePatch } from './place';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded } from '../ui/unsaved';

/** Gdańsk–Sopot–Gdynia: where the map starts without a pin and without the warehouse. */
const TRICITY: GeoPoint = { lat: 54.44, lng: 18.57 };

type Reverse = {
  attribution: string;
  result: { label: string; name: string; lat: number; lng: number } | null;
};

/**
 * The place chosen on a big map (owner, 2026-10-19: "miejsce muszę móc wybrać na mapie"): almost the whole screen
 * on a computer, all of it on a phone; the address search on top; a click or a dragged pin puts the place, and
 * once the pin is dropped the server says the address there (`/api/geo/reverse`, never while dragging).
 * "Użyj tego miejsca" hands the change to the form.
 */
export function MapDialog({ value, onUse, onClose }: { value: Place; onUse: (patch: PlacePatch) => void; onClose: () => void }) {
  // the map starts at the pin, else at the warehouse — so it waits for the settings (it is not moved afterwards)
  const settings = useCompanySettings();
  const center = value.locationPoint ?? settings.data?.warehousePoint ?? null;
  const ref = useRef<HTMLDivElement>(null);
  const [pin, setPin] = useState<{
    point: GeoPoint;
    exact: boolean;
    address: string | null;
    /** A place picked in the Google search: its name and ids (a pin put by hand has none). */
    name?: string;
    picked?: MapChoice['picked'];
  } | null>(null);
  const [query, setQuery] = useState('');
  const { config } = useGeoConfig();
  // the address is asked only for a pin put by hand (a search result already has one)
  const asked = pin?.exact ? pin.point : null;
  const reverse = useQuery({
    queryKey: ['geo-reverse', asked?.lat, asked?.lng],
    enabled: asked !== null,
    retry: false,
    staleTime: Infinity,
    queryFn: () => api<Reverse>(`/api/geo/reverse?lat=${asked!.lat}&lng=${asked!.lng}`),
  });
  // the heading takes the focus; `DialogLayer` handles Escape and gives the focus back
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('h2')?.focus();
  }, []);

  const found = pin?.exact ? (reverse.data?.result ?? null) : null;
  const address = pin?.exact ? (found?.label ?? null) : (pin?.address ?? null);
  const name = pin?.exact ? (found?.name ?? '') : (pin?.name ?? '');
  const waiting = pin?.exact === true && reverse.isFetching;

  return (
    <DialogPortal fullWindow>
      <div className="dialog-backdrop map-dialog-backdrop" onPointerDown={(e) => e.target === e.currentTarget && leaveGuarded(onClose, e.currentTarget)}>
        <div
          ref={ref}
          className="dialog panel map-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Wybierz miejsce na mapie"
          onKeyDown={(e) => {
            if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
          }}
        >
          <div className="map-dialog-head">
            <h2 tabIndex={-1}>Wybierz miejsce na mapie</h2>
            <DialogLayer onClose={onClose} />
            <button className="btn small ghost" type="button" onClick={onClose}>
              Zamknij
            </button>
          </div>
          {config.search === 'google' ? (
            <PlaceSearch
              label="Szukaj miejsca"
              value={query}
              placeholder="Nazwa albo adres, np. Lublin teatr — albo wklej link z Map Google"
              onChange={setQuery}
              onPick={(p) => {
                setQuery(p.name);
                if (p.point) setPin({ point: p.point, exact: false, address: p.address, name: p.name, picked: { googlePlaceId: p.googlePlaceId, venueId: p.venueId, notes: p.notes } });
              }}
            />
          ) : (
            <AddressSearch
              label="Szukaj miejsca"
              value={query}
              placeholder="Adres albo nazwa obiektu, np. Ołowianka 1, Gdańsk"
              onChange={setQuery}
              onPick={(r) =>
                setPin({
                  point: { lat: r.lat, lng: r.lng },
                  exact: false,
                  address: r.label,
                })
              }
            />
          )}
          {settings.isPending ? (
            <div className="map-preview map-dialog-map" aria-busy="true" />
          ) : (
            <MapPreview
              label="Mapa do wyboru miejsca"
              className="map-preview map-dialog-map"
              point={pin?.point ?? value.locationPoint}
              center={center ?? TRICITY}
              centerZoom={center ? 12 : 11}
              wheelZoom
              onPick={(point) => setPin({ point, exact: true, address: null })}
            />
          )}
          <div className="map-dialog-foot">
            <p className="map-dialog-status" role="status">
              {!pin && 'Kliknij mapę albo przeciągnij pinezkę w miejsce wydarzenia (najlepiej przy wjeździe lub scenie).'}
              {waiting && 'Szukam adresu tego miejsca…'}
              {pin && !waiting && address && (
                <>
                  {name && <strong>{name} · </strong>}
                  <span>{address}</span>
                </>
              )}
              {pin && !waiting && !address && (reverse.error instanceof ApiError ? reverse.error.message : 'Nie znaleziono adresu w tym miejscu — wpisz adres ręcznie.')}
            </p>
            <div className="map-dialog-actions">
              <button className="btn" type="button" onClick={onClose}>
                Anuluj
              </button>
              <button
                className="btn primary"
                type="button"
                disabled={!pin || waiting}
                onClick={() => {
                  if (!pin) return;
                  onUse(
                    mapChoicePatch(value, {
                      point: pin.point,
                      exact: pin.exact,
                      address,
                      name,
                      picked: pin.picked,
                    }),
                  );
                  onClose();
                }}
              >
                Użyj tego miejsca
              </button>
            </div>
          </div>
        </div>
      </div>
    </DialogPortal>
  );
}
